import React, { useEffect, useMemo, useState } from "react"
import { Card, CardBody, Col, Container, Modal, Row, Table } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import Select from "react-select"
import moment from "moment"
import { DatePicker } from "antd"
import html2pdf from "html2pdf.js"
import secureLocalStorage from "react-secure-storage"
import { fetchNodeData } from "utils/common-fetchData"
import Loader from "pages/Separate/Loader"
import configure from "configure"
import zithasLogo from "../../../assets/images/zithas-dark-logo.png"

const { RangePicker } = DatePicker

// html2canvas can't reliably render some currency glyphs (₹, د.إ) — use plain-ASCII
// prefixes in exported PDFs/Excel so amounts never render as garbled characters.
const PDF_CURRENCY_SYMBOLS = {
  INR: "Rs.",
  USD: "$",
  GBP: "GBP",
  EUR: "EUR",
  CAD: "CA$",
  AUD: "A$",
  AED: "AED",
  SGD: "S$",
}
const getPdfSymbol = code => PDF_CURRENCY_SYMBOLS[code] || (code ? code : "Rs.")

// jsPDF page size expressed in raw px (unit:"px") so the canvas we hand it
// maps 1:1 — no mm/dpi guesswork that leaves the page under-filled or clipped.
// Content is sized to PDF_CONTENT_WIDTH; PDF_MARGIN is added on every side of
// every page (html2pdf re-applies it per sliced page, unlike CSS padding on
// the source div which only shows up at the very top/bottom of the whole doc).
// Light orange for void invoices and their payments.
const VOID_BG = "#fff1e0"

const PDF_MARGIN = 32
const PDF_CONTENT_WIDTH = 1320
const PDF_PAGE_WIDTH = PDF_CONTENT_WIDTH + PDF_MARGIN * 2
const PDF_PAGE_HEIGHT = 980

const getLogoBase64 = async () => {
  const res = await fetch(zithasLogo)
  const blob = await res.blob()
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.readAsDataURL(blob)
  })
}

const generateFYOptions = () => {
  const now = new Date()
  const currentMonth = now.getMonth() + 1
  const currentYear = now.getFullYear()
  const currentFYStart = currentMonth >= 4 ? currentYear : currentYear - 1

  const options = []
  for (let y = 2018; y <= currentFYStart; y++) {
    options.push({
      label: `FY ${y}-${String(y + 1).slice(-2)}`,
      value: `${y}-${y + 1}`,
    })
  }
  return options.reverse()
}
const FY_OPTIONS = generateFYOptions()

const fmt = (n, symbol = "₹") =>
  `${symbol} ${Math.abs(Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

// FY label for the year a given date falls in (FY runs Apr–Mar)
const fyLabelForDate = date => {
  const m = moment(date)
  const fyStartYear = m.month() + 1 <= 3 ? m.year() - 1 : m.year()
  return `${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`
}

// When a statement spans more than one financial year, insert a "Closing
// Balance" row for the ending FY and an "Opening Balance" row for the next
// FY at every 31-Mar boundary crossed within the period — same running
// balance on both, just marking the year-end/year-start split. Only inserted
// strictly between two real transactions — never as the first or last row —
// so a boundary that falls in a stretch with no activity on one side (e.g.
// the filtered period extends past the last real transaction) is skipped.
const buildDisplayRows = statementRes => {
  const rows = statementRes?.statement || []
  if (!rows.length) return []

  const firstTxnDate = rows[0].date
  const lastTxnDate = rows[rows.length - 1].date

  const boundaries = []
  for (let yr = moment(firstTxnDate).year(); yr <= moment(lastTxnDate).year(); yr++) {
    const candidate = `${yr}-03-31`
    if (candidate > firstTxnDate && candidate < lastTxnDate) boundaries.push(candidate)
  }
  if (!boundaries.length) return rows.map(r => ({ ...r, rowType: "txn" }))

  const display = []
  let lastBalance = statementRes.opening_balance
  let bIdx = 0

  const pushBoundary = boundaryDate => {
    display.push({
      rowType: "closing",
      date: boundaryDate,
      label: `Closing Balance — FY ${fyLabelForDate(boundaryDate)}`,
      balance: lastBalance,
    })
    const nextFYDate = moment(boundaryDate).add(1, "day").format("YYYY-MM-DD")
    display.push({
      rowType: "opening",
      date: nextFYDate,
      label: `Opening Balance — FY ${fyLabelForDate(nextFYDate)}`,
      balance: lastBalance,
    })
  }

  rows.forEach(r => {
    while (bIdx < boundaries.length && boundaries[bIdx] < r.date) {
      pushBoundary(boundaries[bIdx])
      bIdx++
    }
    display.push({ ...r, rowType: "txn" })
    lastBalance = r.balance
  })
  while (bIdx < boundaries.length) {
    pushBoundary(boundaries[bIdx])
    bIdx++
  }

  return display
}

const CustomerStatement = () => {
  document.title = "Customer Statement | Zithas Crm"
  const token = secureLocalStorage.getItem("auth_token")

  const [isLoading, setLoading] = useState(false)
  const [customerOptions, setCustomerOptions] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedFY, setSelectedFY] = useState(FY_OPTIONS[0])
  const [monthRange, setMonthRange] = useState([null, null])
  const [customRangeActive, setCustomRangeActive] = useState(false)
  const [statementRes, setStatementRes] = useState(null)
  // Payment row whose details popup is open (null = closed)
  const [paymentRow, setPaymentRow] = useState(null)

  useEffect(() => {
    const loadCustomers = async () => {
      const res = await fetchNodeData(
        setLoading,
        `ztplPerformaIndex/get/${configure.location[0]}`,
        token
      )
      if (res?.status == 200) {
        setCustomerOptions(
          (res?.data?.customer || []).map(obj => ({
            label: obj?.company,
            value: obj?.id,
          }))
        )
      }
    }
    loadCustomers()
  }, [])

  const effectiveRange = useMemo(() => {
    if (customRangeActive && monthRange[0] && monthRange[1]) {
      return {
        from: monthRange[0].startOf("month").format("YYYY-MM-DD"),
        to: monthRange[1].endOf("month").format("YYYY-MM-DD"),
      }
    }
    const startYear = parseInt(selectedFY?.value?.split("-")[0], 10)
    return { from: `${startYear}-04-01`, to: `${startYear + 1}-03-31` }
  }, [customRangeActive, monthRange, selectedFY])

  useEffect(() => {
    const loadStatement = async () => {
      if (!selectedCustomer) {
        setStatementRes(null)
        return
      }
      const res = await fetchNodeData(
        setLoading,
        `customerStatement/${selectedCustomer.value}?from=${effectiveRange.from}&to=${effectiveRange.to}`,
        token
      )
      setStatementRes(res?.success ? res : null)
    }
    loadStatement()
  }, [selectedCustomer, effectiveRange])

  const onMonthRangeChange = dates => {
    if (!dates) {
      setMonthRange([null, null])
      setCustomRangeActive(false)
      return
    }
    setMonthRange(dates)
    setCustomRangeActive(true)
  }

  const clearCustomRange = () => {
    setCustomRangeActive(false)
    setMonthRange([null, null])
  }

  const symbol = statementRes?.currency_symbol || "₹"
  const displayRows = useMemo(() => buildDisplayRows(statementRes), [statementRes])

  // Rough row-count budget per page — first page also carries the
  // customer/summary blocks so it fits fewer transaction rows. Tune these
  // if content ever overflows a page in practice.
  const ROWS_FIRST_PAGE = 13
  const ROWS_OTHER_PAGE = 23

  const buildStatementHtml = logoSrc => {
    const c = statementRes.customer || {}
    const pdfSym = getPdfSymbol(statementRes.currency)
    const fmtPdf = n => `${pdfSym} ${Math.abs(Number(n) || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
    const signed = n => (n < 0 ? `(${fmtPdf(n)})` : fmtPdf(n))

    const addressLine = c.address || ""
    const cityLine = [c.city, c.state, c.country].filter(Boolean).join(", ") +
      (c.zipcode ? ` - ${c.zipcode}` : "")
    const contactLine = [c.phone ? `Phone: ${c.phone}` : "", c.email ? `Email: ${c.email}` : ""]
      .filter(Boolean)
      .join("  |  ")

    const tile = (label, value, color = "#212529") => `
      <div style="flex:1; background:#f8f7fb; border:1px solid #e2dbe8; border-radius:6px; padding:10px 14px;">
        <div style="font-size:9.5px; text-transform:uppercase; letter-spacing:0.8px; opacity:0.55; margin-bottom:4px;">${label}</div>
        <div style="font-size:14px; font-weight:700; color:${color};">${value}</div>
      </div>`

    const rowHtml = (r, i) => {
      if (r.rowType === "closing" || r.rowType === "opening") {
        return `
          <tr style="background:#f3e5f5;">
            <td colspan="5" style="padding:7px 8px; font-weight:700; font-style:italic;">${r.label}</td>
            <td style="padding:7px 8px; text-align:right; font-weight:700;">${signed(r.balance)}</td>
          </tr>`
      }
      return `
        <tr style="background:${r.is_void ? VOID_BG : i % 2 === 0 ? "#ffffff" : "#faf8fc"};">
          <td style="padding:7px 8px; border-bottom:1px solid #ececec;">${moment(r.date).format("DD-MM-YYYY")}</td>
          <td style="padding:7px 8px; border-bottom:1px solid #ececec; text-transform:capitalize;">${r.type}</td>
          <td style="padding:7px 8px; border-bottom:1px solid #ececec; word-break:break-word;">${r.reference || "—"}</td>
          <td style="padding:7px 8px; border-bottom:1px solid #ececec; text-align:right;">${r.debit ? fmtPdf(r.debit) : "—"}</td>
          <td style="padding:7px 8px; border-bottom:1px solid #ececec; text-align:right;">${r.credit ? fmtPdf(r.credit) : "—"}</td>
          <td style="padding:7px 8px; border-bottom:1px solid #ececec; text-align:right; font-weight:600;">${signed(r.balance)}</td>
        </tr>`
    }

    const pages = []
    const remaining = [...displayRows]
    pages.push(remaining.splice(0, ROWS_FIRST_PAGE))
    while (remaining.length) pages.push(remaining.splice(0, ROWS_OTHER_PAGE))

    const buildPageHtml = (pageRows, pIdx, totalPages) => {
      const isFirst = pIdx === 0
      const isLast = pIdx === totalPages - 1

      const headerBlock = `
        <div style="display:flex; justify-content:space-between; align-items:center; padding-bottom:16px; border-bottom:3px solid #212529; margin-bottom:20px;">
          <img src="${logoSrc}" style="height:46px;" />
          <div style="text-align:right;">
            <div style="font-size:18px; font-weight:700; letter-spacing:0.5px;">ACCOUNT STATEMENT</div>
            <div style="font-size:10px; opacity:0.6; margin-top:2px;">${c.company || ""} &nbsp;•&nbsp; Page ${pIdx + 1} of ${totalPages}</div>
          </div>
        </div>`

      const infoBlock = !isFirst ? "" : `
        <div style="display:flex; justify-content:space-between; gap:24px; margin-bottom:20px;">
          <div style="flex:1;">
            <div style="font-size:10px; text-transform:uppercase; letter-spacing:1px; opacity:0.5; margin-bottom:6px;">Statement For</div>
            <div style="font-size:14px; font-weight:700;">${c.company || ""}</div>
            ${addressLine ? `<div style="margin-top:3px;">${addressLine}</div>` : ""}
            ${cityLine.trim() ? `<div>${cityLine}</div>` : ""}
            ${c.vatnumber ? `<div style="margin-top:3px;">GSTIN/VAT: ${c.vatnumber}</div>` : ""}
            ${contactLine ? `<div style="margin-top:3px;">${contactLine}</div>` : ""}
          </div>
          <div style="flex:0 0 260px; text-align:right;">
            <div style="font-size:10px; text-transform:uppercase; letter-spacing:1px; opacity:0.5; margin-bottom:6px;">Statement Period</div>
            <div style="font-size:13px; font-weight:600;">${moment(statementRes.period.from).format("DD MMM YYYY")} — ${moment(statementRes.period.to).format("DD MMM YYYY")}</div>
            <div style="margin-top:6px; font-size:10px; opacity:0.6;">Currency: ${statementRes.currency}</div>
          </div>
        </div>
        <div style="display:flex; gap:12px; margin-bottom:20px;">
          ${tile("Opening Balance", signed(statementRes.opening_balance))}
          ${tile("Total Debit", fmtPdf(statementRes.total_debit), "#a93226")}
          ${tile("Total Credit", fmtPdf(statementRes.total_credit), "#1e7a4c")}
          ${tile("Closing Balance", signed(statementRes.closing_balance), statementRes.closing_balance >= 0 ? "#a93226" : "#1e7a4c")}
        </div>`

      const openingRow = !isFirst ? "" : `
        <tr style="background:#f3e5f5;">
          <td colspan="5" style="padding:7px 8px; font-weight:700;">Balance Brought Forward</td>
          <td style="padding:7px 8px; text-align:right; font-weight:700;">${signed(statementRes.opening_balance)}</td>
        </tr>`

      const totalRow = !isLast ? "" : `
        <tr style="background:#f3e5f5; font-weight:700; border-top:2px solid #212529;">
          <td colspan="3" style="padding:8px;">Total</td>
          <td style="padding:8px; text-align:right;">${fmtPdf(statementRes.total_debit)}</td>
          <td style="padding:8px; text-align:right;">${fmtPdf(statementRes.total_credit)}</td>
          <td style="padding:8px; text-align:right;">${signed(statementRes.closing_balance)}</td>
        </tr>`

      const footerBlock = !isLast ? "" : `
        <div style="margin-top:16px; font-size:9.5px; opacity:0.55; text-align:center;">
          This is a system-generated statement and does not require a signature.
        </div>`

      return `
        <div class="pdf-page${pIdx > 0 ? " pdf-page-break" : ""}" style="width:${PDF_CONTENT_WIDTH}px; box-sizing:border-box;">
          ${headerBlock}
          ${infoBlock}
          <table style="width:100%; border-collapse:collapse; table-layout:fixed;">
            <colgroup>
              <col style="width:11%"><col style="width:10%"><col style="width:27%">
              <col style="width:17%"><col style="width:17%"><col style="width:18%">
            </colgroup>
            <thead>
              <tr style="background:#212529; color:#fff;">
                <th style="padding:8px; text-align:left;">Date</th>
                <th style="padding:8px; text-align:left;">Type</th>
                <th style="padding:8px; text-align:left;">Reference</th>
                <th style="padding:8px; text-align:right;">Debit</th>
                <th style="padding:8px; text-align:right;">Credit</th>
                <th style="padding:8px; text-align:right;">Balance</th>
              </tr>
            </thead>
            <tbody>
              ${openingRow}
              ${pageRows.map((r, i) => rowHtml(r, i)).join("")}
              ${totalRow}
            </tbody>
          </table>
          ${footerBlock}
        </div>`
    }

    const pagesHtml = pages
      .map((pageRows, pIdx) => buildPageHtml(pageRows, pIdx, pages.length))
      .join("")

    return `<div style="font-family:'Poppins', Arial, sans-serif; font-size:12px; color:#1c1c1c; background:#fff;">${pagesHtml}</div>`
  }

  const onExportPDF = async () => {
    if (!statementRes) return
    setLoading(true)
    try {
      const logoSrc = await getLogoBase64()
      const container = document.createElement("div")
      container.style.cssText = "position:fixed;left:-9999px;top:0;"
      container.innerHTML = buildStatementHtml(logoSrc)
      document.body.appendChild(container)

      const opt = {
        margin: PDF_MARGIN,
        filename: `customer-statement-${(statementRes.customer?.company || "customer").replace(/[^a-z0-9]/gi, "-")}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, letterRendering: true, useCORS: true, windowWidth: PDF_CONTENT_WIDTH },
        jsPDF: { unit: "px", format: [PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT], orientation: "landscape" },
        pagebreak: { mode: ["css"], before: ".pdf-page-break", avoid: "tr" },
      }
      await html2pdf().from(container.firstElementChild).set(opt).save()
      document.body.removeChild(container)
    } catch (err) {
      console.error("Customer statement PDF failed", err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page-content">
      {isLoading && <Loader />}
      <Container fluid>
        <Breadcrumb title="Reports" breadcrumbItem="Customer Statement" />
        <Card>
          <CardBody>
            <Row className="g-3 align-items-end mb-4">
              <Col md={3}>
                <label className="mb-1 text-muted" style={{ fontSize: 13 }}>
                  Customer
                </label>
                <Select
                  placeholder="Select Customer"
                  classNamePrefix="select"
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={setSelectedCustomer}
                />
              </Col>
              <Col md={2}>
                <label className="mb-1 text-muted" style={{ fontSize: 13 }}>
                  Financial Year
                </label>
                <Select
                  classNamePrefix="select"
                  options={FY_OPTIONS}
                  value={selectedFY}
                  isDisabled={customRangeActive}
                  onChange={opt => {
                    setSelectedFY(opt)
                    setCustomRangeActive(false)
                  }}
                />
              </Col>
              <Col md={4}>
                <label className="mb-1 text-muted" style={{ fontSize: 13 }}>
                  Custom Range (Month/Year)
                </label>
                <div className="d-flex gap-2">
                  <RangePicker
                    picker="month"
                    format="MM-YYYY"
                    value={monthRange}
                    onChange={onMonthRangeChange}
                    style={{ width: "100%" }}
                  />
                  {customRangeActive && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={clearCustomRange}
                      type="button"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </Col>
            </Row>

            {!selectedCustomer ? (
              <div className="text-center text-muted py-5">
                <i
                  className="fas fa-user-circle mb-2 d-block"
                  style={{ fontSize: 32 }}
                ></i>
                Select a customer to view their statement
              </div>
            ) : statementRes ? (
              <>
                <div
                  className="d-flex justify-content-between flex-wrap gap-3 mb-3 p-3 rounded"
                  style={{ backgroundColor: "#f8f7fb", border: "1px solid #e2dbe8" }}
                >
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Statement For
                    </div>
                    <div className="fw-bold" style={{ fontSize: 15 }}>
                      {statementRes.customer?.company}
                    </div>
                    {statementRes.customer?.address && (
                      <div style={{ fontSize: 13 }}>{statementRes.customer.address}</div>
                    )}
                    {[statementRes.customer?.city, statementRes.customer?.state, statementRes.customer?.country]
                      .filter(Boolean).join(", ") && (
                        <div style={{ fontSize: 13 }}>
                          {[statementRes.customer?.city, statementRes.customer?.state, statementRes.customer?.country]
                            .filter(Boolean)
                            .join(", ")}
                          {statementRes.customer?.zipcode ? ` - ${statementRes.customer.zipcode}` : ""}
                        </div>
                      )}
                    {statementRes.customer?.vatnumber && (
                      <div style={{ fontSize: 13 }}>GSTIN/VAT: {statementRes.customer.vatnumber}</div>
                    )}
                    {(statementRes.customer?.phone || statementRes.customer?.email) && (
                      <div style={{ fontSize: 13 }}>
                        {[
                          statementRes.customer?.phone && `Phone: ${statementRes.customer.phone}`,
                          statementRes.customer?.email && `Email: ${statementRes.customer.email}`,
                        ]
                          .filter(Boolean)
                          .join("  |  ")}
                      </div>
                    )}
                  </div>
                  <div className="text-md-end">
                    <div className="text-muted mb-1" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Statement Period
                    </div>
                    <div className="fw-semibold" style={{ fontSize: 14 }}>
                      {moment(statementRes.period.from).format("DD MMM YYYY")} — {moment(statementRes.period.to).format("DD MMM YYYY")}
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      Currency: {statementRes.currency}
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
                  <div className="d-flex gap-3 flex-wrap">
                    {[
                      {
                        label: "Opening Balance",
                        value: statementRes.opening_balance,
                      },
                      {
                        label: "Total Invoiced",
                        value: statementRes.total_debit,
                        color: "text-danger",
                      },
                      {
                        label: "Total Received",
                        value: statementRes.total_credit,
                        color: "text-success",
                      },
                      {
                        label: "Closing Balance",
                        value: statementRes.closing_balance,
                      },
                    ].map((box, i) => (
                      <div
                        key={i}
                        className="budget_status pt-2 px-3 pb-2 rounded"
                        style={{ backgroundColor: "#5c038517", minWidth: "160px" }}
                      >
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                          {box.label}
                        </div>
                        <div
                          className={`fw-bold fs-6 mb-0 ${box.color || (box.value < 0 ? "text-success" : "")
                            }`}
                        >
                          {box.value < 0
                            ? `(${fmt(box.value, symbol)})`
                            : fmt(box.value, symbol)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-label export-btn tex-white credit-card-btn"
                      onClick={onExportPDF}
                      type="button"
                    >
                      <i className="fas fa-file-pdf me-2 label-icon"></i>PDF
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table className="table table-bordered mb-0" size="sm">
                    <thead className="text-white" style={{ backgroundColor: "#752a97" }}>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Reference</th>
                        <th className="text-end">Debit</th>
                        <th className="text-end">Credit</th>
                        <th className="text-end">Balance</th>
                        <th className="text-center">View</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ backgroundColor: "#f3e5f5" }}>
                        <td colSpan={5} className="fw-semibold">
                          Balance Brought Forward
                        </td>
                        <td className="text-end fw-semibold">
                          {statementRes.opening_balance < 0
                            ? `(${fmt(statementRes.opening_balance, symbol)})`
                            : fmt(statementRes.opening_balance, symbol)}
                        </td>
                        <td></td>
                      </tr>
                      {displayRows.length > 0 ? (
                        displayRows.map((row, i) =>
                          row.rowType === "closing" || row.rowType === "opening" ? (
                            <tr key={i} style={{ backgroundColor: "#f3e5f5" }}>
                              <td colSpan={5} className="fw-semibold fst-italic">
                                {row.label}
                              </td>
                              <td className="text-end fw-semibold">
                                {row.balance < 0
                                  ? `(${fmt(row.balance, symbol)})`
                                  : fmt(row.balance, symbol)}
                              </td>
                              <td></td>
                            </tr>
                          ) : (
                            <tr
                              key={i}
                              className="row-hover"
                              // Void invoices (and their payments): shown for reference, not in the balance.
                              style={row.is_void ? { "--bs-table-bg": VOID_BG, backgroundColor: VOID_BG } : undefined}
                            >
                              <td>{moment(row.date).format("DD-MM-YYYY")}</td>
                              <td>
                                <span
                                  className={`badge ${row.type === "invoice" ? "bg-danger" : "bg-success"
                                    }`}
                                >
                                  {row.type === "invoice" ? "Invoice" : "Payment"}
                                </span>
                              </td>
                              <td>{row.reference || "—"}</td>
                              <td className="text-end">
                                {row.debit ? fmt(row.debit, symbol) : "—"}
                              </td>
                              <td className="text-end">
                                {row.credit ? fmt(row.credit, symbol) : "—"}
                              </td>
                              <td className="text-end fw-semibold">
                                {row.balance < 0
                                  ? `(${fmt(row.balance, symbol)})`
                                  : fmt(row.balance, symbol)}
                              </td>
                              <td className="text-center">
                                {/* Payment rows: details popup on this page. Invoice rows:
                                    the invoice opens in a new tab, so the statement stays as it is. */}
                                {row.type === "payment" && row.payment ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                                    title="View payment details"
                                    onClick={() => setPaymentRow(row)}
                                  >
                                    <i className="fas fa-eye"></i>
                                  </button>
                                ) : row.invoice_id && row.view_type ? (
                                  <a
                                    href={`${configure.appUrl}finance/view-${row.view_type}/${row.invoice_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                                    title="View invoice"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </a>
                                ) : (
                                  "—"
                                )}
                              </td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4">
                            No transactions in this period
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="fw-bold" style={{ backgroundColor: "#f3e5f5" }}>
                        <td colSpan={3}>Total</td>
                        <td className="text-end">{fmt(statementRes.total_debit, symbol)}</td>
                        <td className="text-end">{fmt(statementRes.total_credit, symbol)}</td>
                        <td className="text-end">
                          {statementRes.closing_balance < 0
                            ? `(${fmt(statementRes.closing_balance, symbol)})`
                            : fmt(statementRes.closing_balance, symbol)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </>
            ) : (
              <div className="text-center text-muted py-5">No data found</div>
            )}
          </CardBody>
        </Card>

        <Modal isOpen={!!paymentRow} toggle={() => setPaymentRow(null)} centered size="md">
          <div className="modal-header">
            <h5 className="modal-title mt-0">Payment Details</h5>
            <button type="button" onClick={() => setPaymentRow(null)} className="btn-modal-close">
              <span className="btn-modal-span">&times;</span>
            </button>
          </div>
          {paymentRow?.payment && (() => {
            const pay = paymentRow.payment
            const sym = paymentRow.currency_symbol || symbol
            const isForeign = paymentRow.currency && paymentRow.currency !== "INR"
            const day = d => (d && moment(d).isValid() ? moment(d).format("DD-MM-YYYY") : "—")
            const line = (label, value) => (
              <tr>
                <th className="text-muted fw-normal" style={{ width: "42%" }}>{label}</th>
                <td>{value === null || value === undefined || value === "" ? "—" : value}</td>
              </tr>
            )
            return (
              <div className="modal-body">
                <Table size="sm" className="mb-3">
                  <tbody>
                    {line("Invoice", paymentRow.reference)}
                    {line("Amount received", fmt(pay.amount, sym))}
                    {line("TDS", pay.tds ? `${fmt(pay.tds, sym)}${pay.tds_percentage ? ` (${pay.tds_percentage}%)` : ""}` : null)}
                    {line("GST", pay.gst ? fmt(pay.gst, sym) : null)}
                    {isForeign && line("Exchange rate", pay.exchange_rate ? `1 ${paymentRow.currency} = ₹${pay.exchange_rate}` : null)}
                    {isForeign && pay.exchange_rate && line("Amount in INR", fmt(pay.amount * pay.exchange_rate, "₹"))}
                    {line("Received on", day(pay.received_on))}
                    {line("Payment mode", pay.payment_mode)}
                    {line("Transaction ID", pay.transaction_id)}
                    {line("Transaction details", pay.transaction_detail)}
                    {line("Entered in system", pay.entered_at && moment(pay.entered_at).isValid() ? moment(pay.entered_at).format("DD-MM-YYYY hh:mm A") : null)}
                  </tbody>
                </Table>

                <h6 className="mb-2">Bank statement</h6>
                {pay.bank_statement ? (
                  <Table size="sm" className="mb-3">
                    <tbody>
                      {line("Account", pay.bank_statement.account)}
                      {line("Statement date", day(pay.bank_statement.date))}
                      {line("Reference", pay.bank_statement.reference)}
                      {line("Description", pay.bank_statement.description)}
                      {line("Amount in statement", fmt(pay.bank_statement.amount, "₹"))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                    Not matched to a bank statement entry.
                  </div>
                )}

                {paymentRow.invoice_id && paymentRow.view_type && (
                  <a
                    href={`${configure.appUrl}finance/view-${paymentRow.view_type}/${paymentRow.invoice_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-purple"
                  >
                    <i className="fas fa-external-link-alt me-2"></i>Open invoice
                  </a>
                )}
              </div>
            )
          })()}
        </Modal>
      </Container>
    </div>
  )
}

export default CustomerStatement
